<script lang="ts" setup>
import { Timestamp } from "firebase/firestore";
import { ref as FirebaseStorageRef, getDownloadURL } from "firebase/storage";
import type { SubmittedDocument } from "types/SubmittedDocument";
import { DocumentState } from "types/enums/DocumentState";
import { DocumentViewMode } from "types/enums/DocumentViewMode.enum";
import { useSubmissionHasUnsupportedFormat } from "~/composables/useSubmissionHasUnsupportedFormat";
import { DocumentSubmissionTypeLabels } from "~/types/enums/DocumentSubmissionType.enum";
import { canReopenStudentDocument } from "~/utils/student-document-editing";

const props = defineProps({
  submittedDocument: {
    type: Object as PropType<SubmittedDocument>,
    required: true,
  },
  // Mode is either teacher or student
  // Limit the value to one of those two
  mode: {
    type: String as PropType<DocumentViewMode>,
    default: DocumentViewMode.teacher,
  },
  isAutoReturnEnabled: {
    type: Boolean,
    default: true,
  },
  isConsolidatedView: {
    type: Boolean,
    default: false,
  },
});

const isDeleting = ref(false);
const isReopening = ref(false);
const isProcessingReopen = ref(false);
const canReopenDocument = computed(() =>
  props.mode === DocumentViewMode.student &&
  canReopenStudentDocument(props.submittedDocument)
);

const onClickDocument = async (event: MouseEvent | KeyboardEvent) => {
  if (!canReopenDocument.value && !isReopening.value) return;
  event.preventDefault();
  event.stopPropagation();
  if (isReopening.value) return;
  popoverController.value.close();
  if (isPublicDemo.value) {
    useBaseAlert("Oops!", "You can’t edit submissions in demo mode.");
    return;
  }
  isReopening.value = true;
  try {
    const confirmed = await useBaseConfirm(
      "Edit Document",
      "Would you like to edit this document?",
      false,
      "Yes",
      "Cancel",
      false
    );
    if (!confirmed || !props.submittedDocument.id) return;
    isProcessingReopen.value = true;
    await useReopenStudentDocument(props.submittedDocument.id);
    await navigateTo(`/documents/student/review/${props.submittedDocument.id}`);
  } catch (error) {
    useBaseAlert("Unable to Edit Document", error instanceof Error ? error.message : "Please try again.");
  } finally {
    isProcessingReopen.value = false;
    isReopening.value = false;
  }
};
const onDelete = async (ev: MouseEvent) => {
  ev.preventDefault();
  ev.stopPropagation();
  if (isDeleting.value) return;

  if (isPublicDemo.value) {
    useBaseAlert("Oops!", "You can’t delete submissions in demo mode.");
    return;
  }

  const response = await useBaseConfirm(
    "Delete Document",
    props.mode === DocumentViewMode.teacher
      ? "Remove this submission? Student-written documents return to an editable draft. Uploaded documents move to the archive."
      : "Are you sure you want to delete this document?"
  );

  if (!response) return;

  const { deleteDocument } = useDocuments();

  if (!props.submittedDocument.id) return;

  isDeleting.value = true;
  try {
    const result = await deleteDocument(props.submittedDocument.id, {
      teacherDeletion: props.mode === DocumentViewMode.teacher,
    });
    useBaseToast(result === "draft" ? "Submission returned to the student’s drafts." : "Submission archived.", "success");
  } catch {
    useBaseToast("Unable to remove submission. Please try again.", "error");
  } finally {
    isDeleting.value = false;
  }
};

const isDocumentReady = computed(() => {
  return useIsDocumentReadyToGrade(props.submittedDocument);
});

const documentPath = computed(() => {
  if (isPublicDemo.value) {
    return undefined;
  }

  // if (hasUnsupportedFormat.value == true) {
  //   return undefined;
  // }

  return useGenerateDocumentPath(
    props.submittedDocument,
    props.mode,
    props.isAutoReturnEnabled
  );
});

const capabilitiesService = useCapabilitiesServiceStore();
const { userCapabilities } = storeToRefs(capabilitiesService);

const cannotGradeDocument = computed(() => {
  return (
    props.mode == DocumentViewMode.teacher &&
    useCanGradeDocument(props.submittedDocument, props.mode) != true
  );
});

const showSubscriptionMessage = computed(() => {
  return (
    cannotGradeDocument.value == true && showPurchasePacksMessage.value != true
  );
});

const showPurchasePacksMessage = computed(() => {
  return (
    cannotGradeDocument.value == true &&
    userCapabilities.value?.hasSubscription == true
  );
});

const nameId = computed(() => {
  return props.submittedDocument.name.toLowerCase().split(" ").join("-");
});
const documentInfo = computed(() => {
  if (props.isConsolidatedView) return [];

  const info = [] as string[];

  if (props.submittedDocument.studentName) {
    info.push(props.submittedDocument.studentName);
  }

  if (props.submittedDocument.assignmentName) {
    info.push(props.submittedDocument.assignmentName);
  }

  return info;
});

const documentState = computed(() => {
  if (props.mode == DocumentViewMode.teacher)
    return props.submittedDocument.state;

  // Here I need to map states for the student view.
  // If is auto return is not enalbed and the document is not returend then we do not want to show the graded state
  if (
    props.isAutoReturnEnabled != true &&
    props.submittedDocument.state == DocumentState.graded &&
    props.submittedDocument.isReturnedToStudent != true
  ) {
    return DocumentState.submitted;
  }

  switch (props.submittedDocument.state) {
    case DocumentState.graded:
    case DocumentState.uploading:
    case DocumentState.converting:
    case DocumentState.pending:
    case DocumentState.studentReview:
    case DocumentState.editing:
      return props.submittedDocument.state;
    case DocumentState.evaluating:
    default:
      return DocumentState.submitted;
  }
});

const canShowDeleteButton = computed(() => {
  if (props.mode == DocumentViewMode.teacher) return true;

  // These states do not allow the student to archive the document
  return (
    [
      DocumentState.converting,
      DocumentState.graded,
      DocumentState.grading,
    ].includes(props.submittedDocument.state) != true
  );
});

const canShowIgnoreButton = computed(() => {
  if (props.mode == DocumentViewMode.student) return false;

  return props.submittedDocument.state == DocumentState.googlePermissionError;
});

const ignoreDocument = async () => {
  props.submittedDocument.state = DocumentState.ignored;
  await useDocuments().updateDocument(props.submittedDocument);
};

const interval = ref<NodeJS.Timer | undefined>();

onMounted(() => {
  if (props.submittedDocument.state == DocumentState.converting) {
    clearSavedInterval();

    interval.value = setInterval(checkForConversionError, 1000);
  } else {
    clearSavedInterval();
  }

  checkValidDocument();
});

const checkValidDocument = async () => {
  if (props.submittedDocument.hasValidatedDocument == true) return;

  if (props.submittedDocument.originalFileMediaHref == undefined) return;

  try {
    // Check if the document actually exists using hte full href to the document (inf firebase storage)
    const storage = useFirebaseStorage();
    const fileRef = FirebaseStorageRef(
      storage,
      props.submittedDocument.originalFileMediaHref
    );
    const exists = await getDownloadURL(fileRef);

    if (exists) {
      props.submittedDocument.hasValidatedDocument = true;
      return;
    }
  } catch (error) {
    //  empty the original file media href
  } finally {
    props.submittedDocument.originalFileMediaHref = undefined;
    useDocuments().updateDocument(props.submittedDocument);
  }
};

const checkForConversionError = () => {
  const timeSinceLastUpdated =
    Date.now() - props.submittedDocument.lastUpdatedTimestamp;

  const isConversionTimeout =
    [DocumentState.converting, DocumentState.uploading].includes(
      props.submittedDocument.state
    ) && timeSinceLastUpdated > 600000;

  if (isConversionTimeout) {
    clearSavedInterval();

    props.submittedDocument.state = DocumentState.conversionFailed;
    useDocuments().updateDocument(props.submittedDocument);

    useReportProblem().reportProblem(
      {
        description: `Document conversion failed on ${useBasicDateTimeFormatter(
          Date.now()
        )}: ${props.submittedDocument.id} - ${props.submittedDocument.name}`,
        urgency: ReportedFeedbackUrgency.low,
        userId: useCurrentUID()!,
        userEmail: useCurrentUserEmail()!,
        createdAtTimestamp: Timestamp.now(),
        url: window.location.href,
        type: "problem",
        category: "conversionTimeout",
      },
      []
    );
  }
};

const clearSavedInterval = () => {
  if (interval.value) {
    clearInterval(interval.value as any);
  }
};

onUnmounted(() => {
  clearSavedInterval();
});

const appUserData = useAppUserDataStore();
const { isPartOfAnyOrganization, hasLMSConfig, isPublicDemo } =
  storeToRefs(appUserData);

const canEditDocument = computed(() => {
  return props.mode === DocumentViewMode.teacher && hasLMSConfig.value == true;
});

const onEditDocument = () => {
  useOpenDocumentEditModal(props.submittedDocument);
};

const onViewGoogleDriveFile = () => {
  window.open(
    `https://drive.google.com/file/d/${props.submittedDocument.googleDriveFileId}/view`
  );
};

const onClickView = () => {
  if (isPublicDemo.value) {
    useBaseAlert("Oops!", "You can’t grade submissions in demo mode.");
    return;
  }
};
const documentSubmissionType = computed(() => {
  return (
    props.submittedDocument.documentSubmissionType ??
    DocumentSubmissionType.finalDraft
  );
});

const sheetController = useNewSheetController();

const docextension = computed(() => {
  const fileNameParts = props.submittedDocument.name.split(".");
  const extension = fileNameParts[fileNameParts.length - 1];
  return extension;
});

const hasUnsupportedFormat = computed(() => {
  return useSubmissionHasUnsupportedFormat(props.submittedDocument) && false;
});

const showSubmmissionType = computed(() => {
  if (props.isConsolidatedView == true) return false;

  if (props.submittedDocument.assignmentType == undefined) return true;

  return (
    props.submittedDocument.assignmentType == AssignmentType.iewComposition
  );
});

const hasOriginalFileMediaHref = computed(() => {
  return props.submittedDocument.originalFileMediaHref != undefined;
});

const downloadOriginalFile = () => {
  if (hasOriginalFileMediaHref.value == false) return;

  window.open(props.submittedDocument.originalFileMediaHref, "_blank");
};

const onClickCornerBadge = () => {
  if (props.submittedDocument.googleDriveFileId) {
    onViewGoogleDriveFile();
  } else if (hasOriginalFileMediaHref.value) {
    downloadOriginalFile();
  }
};

const popoverController = useNewDialogController();

const onClickPopover = () => {
  popoverController.value.open();
};

const onClickEdit = () => {
  sheetController.value.open();
};
</script>

<template>
  <NuxtLink
    :to="documentPath"
    target="_blank"
    :tabindex="canReopenDocument ? 0 : undefined"
    @click="onClickDocument"
    @keydown.enter.self="onClickDocument"
  >
    <div
      class="flex flex-col rounded bg-surface border-b last:border-none justify-center relative overflow-hidden"
      :class="{
        'cursor-pointer hover:bg-light-default': isDocumentReady || canReopenDocument,
        'p-4 pr-2': !isConsolidatedView,
        'p-2 pr-2': isConsolidatedView,
      }"
      :data-step-id="nameId"
    >
      <div
        v-if="
          (submittedDocument.googleDriveFileId || hasOriginalFileMediaHref) &&
          isConsolidatedView != true &&
          [DocumentState.converting, DocumentState.uploading].includes(
            submittedDocument.state
          ) != true
        "
        class="absolute top-0 right-0 rounded-bl-lg p-1.5 px-2 flex flex-row items-center gap-2 cursor-pointer transition-colors"
        :class="{
          'bg-google-default hover:bg-google-shade':
            submittedDocument.googleDriveFileId,
          'bg-[#2b579a] hover:bg-[#22457a]':
            !submittedDocument.googleDriveFileId && hasOriginalFileMediaHref,
        }"
        @click.stop.prevent="onClickCornerBadge"
      >
        <template v-if="submittedDocument.googleDriveFileId">
          <span
            v-tooltip="'Open in Google Drive'"
            class="flex flex-row items-center gap-2"
          >
            <BaseIconGoogleFile size="18" class="text-google-on" />
            <BaseIconExternalLink class="text-white" size="14" />
          </span>
        </template>
        <template
          v-else-if="
            hasOriginalFileMediaHref &&
            submittedDocument.hasValidatedDocument == true
          "
        >
          <span
            class="flex flex-row items-center gap-2"
            v-tooltip="'Download Word Document'"
          >
            <BaseIconWordFile class="text-white" size="18" />
            <BaseIconDownload class="text-white" size="14" />
          </span>
        </template>
      </div>
      <span
        class="flex flex-row items-center justify-between w-full"
        :class="{
          'min-h-[80px]': !isConsolidatedView,
          'min-h-[40px]': isConsolidatedView,
        }"
      >
        <div class="flex flex-row items-center grow pr-4 overflow-hidden">
          <div class="flex flex-col w-full overflow-hidden text-ellipsis">
            <span
              class="font-bold w-full whitespace-nowrap overflow-hidden truncate text-ellipsis flex flex-row items-center"
              :class="{
                'text-h6': !isConsolidatedView,
                'text-body-1': isConsolidatedView,
              }"
            >
              <span class="text-warning-default" v-if="showSubmmissionType">
                {{
                  DocumentSubmissionTypeLabels[documentSubmissionType]
                    .shortLabel
                }}
              </span>
              <span class="mx-1" v-if="showSubmmissionType"> | </span>
              <span class="overflow-hidden text-ellipsis grow">
                <span>
                  {{ submittedDocument.name }}
                </span>
              </span>
            </span>

            <div class="flex flex-row items-center">
              <span
                v-for="(info, index) in documentInfo"
                :key="index"
                class="border-r last:border-none mr-2 pr-2 last-pr-0 last:mr-0 text-body-1"
              >
                {{ info }}
              </span>
            </div>
            <span
              v-if="submittedDocument.submittedAtTimestamp"
              class="text-muted-foreground"
              :class="{
                'text-body-2': !isConsolidatedView,
                'text-caption': isConsolidatedView,
              }"
            >
              Submitted:
              {{
                useBasicDateTimeFormatter(
                  submittedDocument.submittedAtTimestamp.toMillis
                    ? submittedDocument.submittedAtTimestamp.toMillis()
                    : (submittedDocument.submittedAtTimestamp as any)
                )
              }}
            </span>
          </div>
        </div>
        <div class="flex flex-row items-center shrink-0 justify-end">
          <span class="flex flex-row items-center justify-between">
            <span class="flex flex-row items-center">
              <span
                v-if="
                  submittedDocument.state == DocumentState.uploading ||
                  submittedDocument.state == DocumentState.converting
                "
                class="mr-2"
              >
                <icon name="svg-spinners:ring-resize" size="24" />
              </span>

              <span
                v-if="submittedDocument.isLate == true"
                class="mr-4 text-danger-default"
              >
                late
              </span>
            </span>
            <span
              v-if="
                submittedDocument.enableSubmissionBadges == true &&
                submittedDocument.documentSubmissionType ==
                  DocumentSubmissionType.finalDraft
              "
              class="mr-2"
            >
              <span v-if="submittedDocument.badge">
                <img
                  :src="`/img/badges/${submittedDocument.badge}_badge.png`"
                  :alt="submittedDocument.badge"
                  class="w-8 h-8"
                />
              </span>
            </span>
            <div
              class="flex items-center"
              :class="{
                'flex-row gap-2': true,
              }"
            >
              <DocumentStatePill
                v-if="hasUnsupportedFormat == false"
                :key="documentState"
                :state="isProcessingReopen ? DocumentState.submitted : documentState"
                :is-loading="isProcessingReopen"
                :is-consolidated-view="true"
              />
              <BaseTextButton
                v-if="canShowIgnoreButton"
                @click="ignoreDocument"
              >
                Ignore
              </BaseTextButton>

              <DocumentGradedStatePill
                v-if="
                  hasUnsupportedFormat == false &&
                  documentState == DocumentState.graded &&
                  submittedDocument.documentGradedState != undefined &&
                  useShowDocumentGradedState(submittedDocument) == true
                "
                :document-graded-state="submittedDocument.documentGradedState"
                :is-consolidated-view="true"
              />
              <span
                v-if="
                  documentState == DocumentState.graded &&
                  useShowDocumentGradedState(submittedDocument) != true &&
                  submittedDocument.earnedPoints != undefined &&
                  submittedDocument.maxPoints != undefined
                "
                class="border-2 bg-light-default rounded shrink-0 text-center flex items-center justify-center"
                :class="{
                  'py-[2px] px-[4px] text-body-2': true ,
                }"
              >
                {{ submittedDocument.earnedPoints }} /
                {{ submittedDocument.maxPoints }}
              </span>
            </div>
          </span>
          <div class="flex gap-1 flex-row shrink-0">
            <BaseSheetComponent
              width="600px"
              :z-index-override="9999"
              :include-content-padding="false"
              :controller="sheetController"
            >
              <template #content>
                <DocumentsEditModal
                  :document="submittedDocument"
                  @close="sheetController.close"
                />
              </template>
            </BaseSheetComponent>
            <BasePopover
              v-if="canEditDocument || canReopenDocument || canShowDeleteButton"
              :controller="popoverController"
              align="end"
            >
              <BaseIconButton
                @click.stop.prevent="onClickPopover"
                class="!p-1"
                :size="25"
              >
                <BaseIconMoreVert size="30" />
              </BaseIconButton>
              <template #content>
                <div class="flex flex-col gap-2 p-1 w-[100px]">
                  <BasePopoverMenuItem
                    v-if="canReopenDocument"
                    @click.stop.prevent="onClickDocument"
                  >
                    <template #icon>
                      <BaseIconEdit />
                    </template>
                    Edit
                  </BasePopoverMenuItem>
                  <BasePopoverMenuItem
                    v-if="canEditDocument"
                    @click="onClickEdit"
                  >
                    <template #icon>
                      <BaseIconEdit />
                    </template>
                    Edit
                  </BasePopoverMenuItem>

                  <BasePopoverMenuItem
                    v-if="canShowDeleteButton"
                    @click="onDelete"
                    class="text-danger"
                  >
                    <template #icon>
                      <BaseIconDelete class="text-danger" />
                    </template>
                    <span class="text-danger">Delete</span>
                  </BasePopoverMenuItem>
                </div>
              </template>
            </BasePopover>
          </div>
        </div>
      </span>
      <!-- <BaseLabelBadge
        v-if="hasUnsupportedFormat == true"
        color="danger"
        class="mt-4"
      >
        <template #icon>
          <BaseIconAlert />
        </template>
        The student has submitted an invalid document format. Please have the
        student resubmit a Google Doc or a Word document.
      </BaseLabelBadge> -->
      <BaseLabelBadge
        v-if="submittedDocument.state == DocumentState.conversionFailed"
        color="danger"
        class="mt-4"
      >
        <template #icon>
          <BaseIconAlert />
        </template>

        An error occurred. Delete this submission and try again.
      </BaseLabelBadge>
      <BaseLabelBadge
        v-if="showSubscriptionMessage"
        class="mt-4"
        color="warning"
        alignItems="center"
      >
        <template #icon>
          <BaseIconAlert />
        </template>
        <div class="flex flex-row items-center justify-between">
          <span v-if="isPartOfAnyOrganization != true">
            You need to subscribe to IEW Gradebook to grade your own
            submissions.
          </span>
          <span v-if="isPartOfAnyOrganization">
            You do not have an active IEW Gradebook license. Ask your
            organization administrator to provide a license.
          </span>
          <NuxtLink
            v-if="isPartOfAnyOrganization != true"
            to="/order/cart/create"
            class="shrink-0"
          >
            <BaseOutlinedButton color="warning"> Subscribe </BaseOutlinedButton>
          </NuxtLink>
        </div>
      </BaseLabelBadge>
      <BaseLabelBadge
        v-if="showPurchasePacksMessage"
        class="mt-4"
        color="warning"
        alignItems="center"
      >
        <template #icon>
          <BaseIconAlert />
        </template>
        <div class="flex flex-row items-center justify-between gap-2">
          <span v-if="isPartOfAnyOrganization != true">
            You have run out of submissions for the month. Please upgrade your
            plan or purchase extra submissions to grade this document.
          </span>
          <span v-if="isPartOfAnyOrganization">
            You have run out of submissions for the month. Please ask your
            organization administrator to upgrade your plan or purchase a
            submission pack to grade this document.
          </span>
          <NuxtLink
            v-if="isPartOfAnyOrganization != true"
            to="/configure/subscription"
            class="shrink-0"
          >
            <BaseOutlinedButton color="warning">
              Change Subscription
            </BaseOutlinedButton>
          </NuxtLink>
        </div>
      </BaseLabelBadge>
    </div>
  </NuxtLink>
</template>

<style scoped></style>
